import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import AdminLayout from '../../components/AdminLayout';
import {
  FaImage, FaUpload, FaLink, FaTag, FaStar, FaTrash,
  FaArrowLeft, FaSave, FaGlobe, FaAlignLeft, FaFolder, FaTimes
} from 'react-icons/fa';

function ResourceForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '', description: '', link: '', icon_url: '', category_id: '', tags: '', is_featured: false
  });
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data));
    if (isEdit) {
      setLoading(true);
      api.get(`/resources/admin/all`).then(res => {
        const r = res.data.resources.find(r => r.id === parseInt(id));
        if (r) {
          setForm({
            name: r.name, description: r.description || '', link: r.link,
            icon_url: r.icon_url || '', category_id: r.category_id || '', tags: r.tags || '', is_featured: r.is_featured === 1
          });
          // Set existing icon as preview
          if (r.icon_url) {
            setIconPreview(r.icon_url.startsWith('http') ? r.icon_url : r.icon_url);
          }
        }
        setLoading(false);
      });
    }
  }, [id, isEdit]);

  // Generate preview when file is selected
  useEffect(() => {
    if (!iconFile) return;
    const url = URL.createObjectURL(iconFile);
    setIconPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [iconFile]);

  // Update preview when icon_url changes (manual URL input)
  useEffect(() => {
    if (!iconFile && form.icon_url) {
      setIconPreview(form.icon_url);
    }
  }, [form.icon_url, iconFile]);

  const handleFileSelect = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setIconFile(file);
    setForm(f => ({ ...f, icon_url: '' })); // Clear URL when file is uploaded
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const clearIcon = () => {
    setIconFile(null);
    setIconPreview(null);
    setForm(f => ({ ...f, icon_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (key === 'is_featured') formData.append(key, val ? '1' : '0');
        else formData.append(key, val);
      });
      if (iconFile) formData.append('icon', iconFile);

      if (isEdit) {
        await api.put(`/resources/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        setSuccess('Resource updated successfully!');
      } else {
        await api.post('/resources', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        setSuccess('Resource created successfully!');
        setTimeout(() => navigate('/admin/resources'), 1200);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save resource.');
    }
    setSaving(false);
  };

  // Get favicon from link for fallback
  const getFavicon = () => {
    if (!form.link) return null;
    try {
      const domain = new URL(form.link).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
    } catch { return null; }
  };

  const currentPreview = iconPreview || getFavicon();

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div className="rf-loading-spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div className="rf-page-header">
        <button className="rf-back-btn" onClick={() => navigate('/admin/resources')}>
          <FaArrowLeft /> Back
        </button>
        <div>
          <h2 className="rf-title">{isEdit ? 'Edit Resource' : 'New Resource'}</h2>
          <p className="rf-subtitle">{isEdit ? 'Update the resource details below' : 'Fill in the details to add a new resource'}</p>
        </div>
      </div>

      {/* Alerts */}
      {error && <div className="rf-alert rf-alert-error"><FaTimes /> {error}</div>}
      {success && <div className="rf-alert rf-alert-success"><FaStar /> {success}</div>}

      <form onSubmit={handleSubmit} className="rf-form">
        <div className="rf-layout">
          {/* Left Column - Main Info */}
          <div className="rf-main-col">
            {/* Name */}
            <div className="rf-field">
              <label className="rf-label"><FaTag /> Name <span className="rf-required">*</span></label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                required
                className="rf-input"
                placeholder="e.g. Semrush, Ahrefs, ChatGPT..."
              />
            </div>

            {/* Link */}
            <div className="rf-field">
              <label className="rf-label"><FaGlobe /> Website URL <span className="rf-required">*</span></label>
              <div className="rf-input-with-preview">
                <input
                  type="url"
                  value={form.link}
                  onChange={e => setForm({ ...form, link: e.target.value })}
                  required
                  className="rf-input"
                  placeholder="https://example.com"
                />
                {form.link && (
                  <div className="rf-url-favicon">
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${(() => { try { return new URL(form.link).hostname; } catch { return ''; }})()}&sz=32`}
                      alt=""
                      onError={e => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="rf-field">
              <label className="rf-label"><FaAlignLeft /> Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                className="rf-input rf-textarea"
                rows="4"
                placeholder="Brief description of this resource..."
              />
            </div>

            {/* Category + Tags Row */}
            <div className="rf-row">
              <div className="rf-field">
                <label className="rf-label"><FaFolder /> Category</label>
                <select
                  value={form.category_id}
                  onChange={e => setForm({ ...form, category_id: e.target.value })}
                  className="rf-input rf-select"
                >
                  <option value="">-- Select Category --</option>
                  {(() => {
                    const parents = categories.filter(c => !c.parent_id);
                    const children = categories.filter(c => c.parent_id);
                    return parents.map(p => (
                      <React.Fragment key={p.id}>
                        <option value={p.id}>{p.name}</option>
                        {children.filter(c => c.parent_id === p.id).map(c => (
                          <option key={c.id} value={c.id}>&nbsp;&nbsp;└ {c.name}</option>
                        ))}
                      </React.Fragment>
                    ));
                  })()}
                </select>
              </div>
              <div className="rf-field">
                <label className="rf-label"><FaTag /> Tags</label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={e => setForm({ ...form, tags: e.target.value })}
                  className="rf-input"
                  placeholder="seo, marketing, ai..."
                />
              </div>
            </div>
          </div>

          {/* Right Column - Icon & Settings */}
          <div className="rf-side-col">
            {/* Icon Upload Card */}
            <div className="rf-card">
              <h3 className="rf-card-title"><FaImage /> Resource Icon</h3>

              {/* Preview */}
              <div className="rf-icon-preview-area">
                {currentPreview ? (
                  <div className="rf-icon-preview-wrap">
                    <img
                      src={currentPreview}
                      alt="Icon preview"
                      className="rf-icon-preview-img"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                    <button type="button" className="rf-icon-remove" onClick={clearIcon} title="Remove icon">
                      <FaTimes />
                    </button>
                    <span className="rf-icon-badge">
                      {iconFile ? 'New Upload' : 'Current Icon'}
                    </span>
                  </div>
                ) : (
                  <div className="rf-icon-placeholder">
                    <FaImage />
                    <span>No icon set</span>
                  </div>
                )}
              </div>

              {/* Drag & Drop Zone */}
              <div
                className={`rf-dropzone ${dragOver ? 'rf-dropzone-active' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <FaUpload className="rf-dropzone-icon" />
                <span className="rf-dropzone-text">
                  {dragOver ? 'Drop image here' : 'Click or drag to upload'}
                </span>
                <span className="rf-dropzone-hint">PNG, JPG, SVG, WebP</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={e => handleFileSelect(e.target.files[0])}
                  hidden
                />
              </div>

              {/* OR divider */}
              <div className="rf-or-divider">
                <span>or paste URL</span>
              </div>

              {/* Icon URL Input */}
              <div className="rf-icon-url-input">
                <FaLink className="rf-icon-url-prefix" />
                <input
                  type="url"
                  value={form.icon_url}
                  onChange={e => {
                    setForm({ ...form, icon_url: e.target.value });
                    setIconFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="rf-input"
                  placeholder="https://example.com/icon.png"
                  disabled={!!iconFile}
                />
              </div>
            </div>

            {/* Settings Card */}
            <div className="rf-card">
              <h3 className="rf-card-title"><FaStar /> Settings</h3>

              <label className="rf-toggle-wrap">
                <div className={`rf-toggle ${form.is_featured ? 'rf-toggle-on' : ''}`}
                  onClick={() => setForm({ ...form, is_featured: !form.is_featured })}
                >
                  <div className="rf-toggle-knob" />
                </div>
                <div>
                  <span className="rf-toggle-label">Featured Resource</span>
                  <span className="rf-toggle-hint">Show in spotlight section</span>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="rf-actions">
          <button type="button" onClick={() => navigate('/admin/resources')} className="rf-btn rf-btn-cancel">
            Cancel
          </button>
          <button type="submit" className="rf-btn rf-btn-save" disabled={saving}>
            {saving ? (
              <><div className="rf-btn-spinner" /> Saving...</>
            ) : (
              <><FaSave /> {isEdit ? 'Update Resource' : 'Create Resource'}</>
            )}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

export default ResourceForm;
